import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, doc, getDoc, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import PendingSessionsWidget from './PendingSessionsWidget';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [providers, setProviders] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    skill: ''
  });

  const skillOptions = [
    'Interview Preparation',
    'Public Speaking',
    'Dating/Flirting',
    'Business Communication',
    'Language Practice',
    'Social Skills',
    'Presentation Skills',
    'Networking',
    'Confidence Building',
    'Accent Training'
  ];

  useEffect(() => {
    fetchProviders();
    if (currentUser) {
      fetchUserProfile();
      fetchUpcomingSessions();
    }
  }, [currentUser]);

  const fetchUserProfile = async () => {
    try {
      const docRef = doc(db, 'users', currentUser.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setUserProfile(docSnap.data());
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchProviders = async () => {
    try {
      const q = query(
        collection(db, 'users'),
        where('userType', 'in', ['provider', 'both']),
        where('isProfileComplete', '==', true),
        where('emailVerified', '==', true),
        limit(12)
      );
      
      const querySnapshot = await getDocs(q);
      const providersData = [];
      
      querySnapshot.forEach((doc) => {
        if (currentUser && doc.id === currentUser.uid) {
          return;
        }
        
        const providerData = {
          id: doc.id,
          ...doc.data()
        };
        providersData.push(providerData);
      });
      
      // Sort by rating client-side and take top 6
      providersData.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
      
      setProviders(providersData.slice(0, 6));
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      // Ensure loading is set to false regardless of authentication status
      setLoading(false);
    }
  };

  const fetchUpcomingSessions = async () => {
    if (!currentUser) return;
    
    try {
      // Fetch sessions where user is practitioner
      const practitionerQuery = query(
        collection(db, 'sessions'),
        where('practitionerId', '==', currentUser.uid),
        where('status', 'in', ['scheduled', 'confirmed', 'in_progress'])
      );
      
      // Fetch sessions where user is provider
      const providerQuery = query(
        collection(db, 'sessions'),
        where('providerId', '==', currentUser.uid),
        where('status', 'in', ['scheduled', 'confirmed', 'in_progress'])
      );
      
      const [practitionerSnapshot, providerSnapshot] = await Promise.all([
        getDocs(practitionerQuery),
        getDocs(providerQuery)
      ]);
      
      const allSessions = [];
      
      practitionerSnapshot.forEach(doc => {
        allSessions.push({
          id: doc.id,
          ...doc.data(),
          userRole: 'practitioner'
        });
      });
      
      providerSnapshot.forEach(doc => {
        allSessions.push({
          id: doc.id,
          ...doc.data(),
          userRole: 'provider'
        });
      });
      
      // Sort by creation date and take latest 5
      allSessions.sort((a, b) => {
        const aTime = a.createdAt?.seconds || Date.now() / 1000;
        const bTime = b.createdAt?.seconds || Date.now() / 1000;
        return bTime - aTime;
      });
      
      setUpcomingSessions(allSessions.slice(0, 5));
    } catch (error) {
      console.error('Error fetching upcoming sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const filteredProviders = providers.filter(provider => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const fullName = `${provider.firstName || ''} ${provider.lastName || ''}`.toLowerCase();
      const bio = (provider.bio || '').toLowerCase();
      
      const matchesSearch = fullName.includes(searchTerm) || bio.includes(searchTerm);
      if (!matchesSearch) return false;
    }
    
    if (filters.skill && !provider.skills?.includes(filters.skill)) return false;
    
    return true;
  });

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(dateObj);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return '#f59e0b';
      case 'confirmed': return '#3b82f6';
      case 'in_progress': return '#059669';
      default: return '#6b7280';
    }
  };

  const isProvider = userProfile?.userType === 'provider' || userProfile?.userType === 'both';

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>Welcome to Practice Talking</h1>
        <div className="user-info">
          Your Voice. Your Practice. Your Power.
          {/* {currentUser ? (
            <span>Hello, {userProfile?.firstName || currentUser.email}</span>
          ) : (
            <span>Login or Signup to Practice Talking and/or become a Provider</span>
          )} */}
        </div>
      </header>
      
      {/* Pending Sessions Widget - Full Width at Top */}
      {currentUser && isProvider && (
        <div className="pending-sessions-section">
          <PendingSessionsWidget />
        </div>
      )}
      
      <main className="dashboard-main">
        <div className="dashboard-container">
          {/* Left Column - Find a Provider */}
          <div className="dashboard-left">
            <section className="find-provider-section">
              <h2>Find a Provider</h2>
              <p>Connect with expert conversation partners to practice and improve your skills</p>
              
              {/* Search and Filters */}
              <div className="homepage-filters">
                <div className="search-bar">
                  <input
                    type="text"
                    placeholder="Search by name or bio..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="form-input search-input"
                  />
                </div>
                
                <div className="filter-group">
                  <select 
                    value={filters.skill} 
                    onChange={(e) => handleFilterChange('skill', e.target.value)}
                    className="form-select"
                  >
                    <option value="">All Skills</option>
                    {skillOptions.map(skill => (
                      <option key={skill} value={skill}>{skill}</option>
                    ))}
                  </select>
                </div>
                
                <button 
                  onClick={() => navigate('/providers')}
                  className="btn-secondary"
                >
                  Advanced Search
                </button>
              </div>

              {/* Provider Results */}
              <div className="homepage-providers">
                {loading ? (
                  <div className="loading-state">Loading providers...</div>
                ) : filteredProviders.length === 0 ? (
                  <div className="no-results">
                    <p>No providers found. <button onClick={() => navigate('/providers')} className="link-btn">Browse all providers</button></p>
                  </div>
                ) : (
                  <div className="providers-grid">
                    {filteredProviders.slice(0, 4).map(provider => (
                      <div key={provider.id} className="provider-card-mini">
                        <div className="provider-header">
                          <div className="provider-avatar">
                            {provider.profilePicture ? (
                              <img src={provider.profilePicture} alt={`${provider.firstName} ${provider.lastName}`} />
                            ) : (
                              <div className="avatar-placeholder">
                                {provider.firstName?.[0]}{provider.lastName?.[0]}
                              </div>
                            )}
                          </div>
                          <div className="provider-info">
                            <h4>{provider.firstName} {provider.lastName}</h4>
                            <div className="provider-rating">
                              <span className="rating-stars">
                                {'★'.repeat(Math.floor(provider.averageRating || 0))}
                                {'☆'.repeat(5 - Math.floor(provider.averageRating || 0))}
                              </span>
                              <span className="rating-text">
                                {provider.averageRating?.toFixed(1) || 'New'}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="provider-details">
                          {provider.skills && provider.skills.length > 0 && (
                            <div className="provider-skills">
                              <strong>Skills:</strong> 
                              <span>{provider.skills.slice(0, 2).join(', ')}{provider.skills.length > 2 ? '...' : ''}</span>
                            </div>
                          )}
                          
                          {provider.languages && provider.languages.length > 0 && (
                            <div className="provider-languages">
                              <strong>Languages:</strong> 
                              <span>{provider.languages.slice(0, 2).join(', ')}{provider.languages.length > 2 ? '...' : ''}</span>
                            </div>
                          )}
                          
                          {provider.expertise && provider.expertise.length > 0 && (
                            <div className="provider-expertise">
                              <strong>Expertise:</strong> 
                              <span>{provider.expertise.slice(0, 2).join(', ')}{provider.expertise.length > 2 ? '...' : ''}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="provider-rate">${provider.hourlyRate || 0}/hour</div>
                        <button 
                          className="view-profile-btn-mini"
                          onClick={() => navigate(`/provider/${provider.username || provider.id}`)}
                        >
                          View Profile
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* Right Column - Sessions & Provider Navigation */}
          <div className="dashboard-right">
            {/* Provider Profile Widget */}
            <div className="provider-widget">
              {isProvider ? (
                <div className="provider-profile-widget">
                  <h3>My Provider Profile</h3>
                  <p>Manage your profile and availability</p>
                  <button 
                    onClick={() => navigate('/provider-profile')}
                    className="btn-primary"
                  >
                    Manage Profile
                  </button>
                </div>
              ) : (
                <div className="become-provider-widget">
                  <h3>Become a Provider</h3>
                  <p>Share your expertise and earn money</p>
                  <button 
                    onClick={() => navigate('/provider-profile')}
                    className="btn-primary"
                  >
                    Become a Provider
                  </button>
                </div>
              )}
            </div>

            {/* Upcoming Sessions */}
            <div className="upcoming-sessions">
              <h3>Upcoming Sessions</h3>
              {!currentUser ? (
                <div className="login-prompt">
                  <p>Login or Signup to book sessions</p>
                  <button 
                    onClick={() => navigate('/login')}
                    className="btn-primary"
                  >
                    Sign In
                  </button>
                </div>
              ) : loading ? (
                <div className="loading-state">Loading sessions...</div>
              ) : upcomingSessions.length === 0 ? (
                <div className="no-sessions">
                  <p>No upcoming sessions</p>
                  <button 
                    onClick={() => navigate('/providers')}
                    className="btn-secondary"
                  >
                    Book a Session
                  </button>
                </div>
              ) : (
                <>
                  <div className="sessions-list">
                    {upcomingSessions.map(session => (
                      <div key={session.id} className="session-card-mini">
                        <div className="session-header">
                          <div className="session-info">
                            <h4>{session.sessionType}</h4>
                            <span className="session-role">
                              As {session.userRole}
                            </span>
                          </div>
                          <div 
                            className="session-status"
                            style={{ backgroundColor: getStatusColor(session.status) }}
                          >
                            {session.status.replace('_', ' ')}
                          </div>
                        </div>
                        <div className="session-details">
                          <p className="session-partner">
                            {session.userRole === 'practitioner' ? session.providerName : session.practitionerName}
                          </p>
                          <p className="session-time">
                            {formatDate(session.createdAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="sessions-actions">
                    <button 
                      onClick={() => navigate('/practitioner-sessions')}
                      className="btn-secondary"
                    >
                      View My Sessions
                    </button>
                    {isProvider && (
                      <button 
                        onClick={() => navigate('/provider-sessions')}
                        className="btn-secondary"
                      >
                        View Provider Sessions
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;